export const en = {
  common: {
    nav: {
      overview: "Overview",
      account: "My identity",
      apps: "Apps & consents",
      security: "Security",
      admin: "Tenant admin",
      platform: "Platform",
      settings: "Settings",
      docs: "Integration docs",
      developer: "Developer console",
      support: "Tickets",
      announcements: "Announcements",
      withdrawal: "Withdrawals",
    },
    buttons: {
      save: "Save",
      cancel: "Cancel",
      delete: "Delete",
      confirm: "Confirm",
      retry: "Retry",
      logout: "Sign out",
      savePreferences: "Save preferences",
      saveSettings: "Save display settings",
    },
  },
  auth: {
    loginTitle: "Sign in to your workspace",
    loginSubtitle: "Use your tenant account to enter OwO! Identity.",
    tenant: "Tenant slug",
    login: "Username or email",
    password: "Password",
    submit: "Sign in",
    forgotPassword: "Forgot password",
    mfaTitle: "Multi-factor verification",
    backToLogin: "Back to sign in",
  },
  settings: {
    eyebrow: "Account preferences",
    title: "Display settings",
    description: "These options follow your account after sign-in on other browsers.",
    language: "Interface language",
    languageHint: "Takes effect immediately",
    compact: "Compact tables",
    compactHint: "Show more rows on one screen",
    reducedMotion: "Reduce motion",
    reducedMotionHint: "Limit non-essential transitions",
    theme: "Appearance",
    themeHint: "Light, dark, or system; stored with your account",
    themeLight: "Light",
    themeDark: "Dark",
    themeSystem: "System",
    saved: "Display settings saved",
    saveFailed: "Save failed",
  },
  account: {
    preferencesTitle: "Interface & notifications",
    preferencesHint: "Preferences only affect your account, not tenant policy.",
    locale: "Language",
    timezone: "Timezone",
    saved: "Preferences saved",
    saveFailed: "Save failed",
    loading: "Loading preferences",
    loadFailed: "Unable to load preferences",
    eyebrow: "Personal identity",
    title: "Profile and settings",
    description: "Keep the identity others see, and the details attached to your account.",
    export: "Export my data",
    profileTab: "Profile",
    emailsTab: "Email addresses",
    addressesTab: "Addresses",
    preferencesTab: "Preferences",
    billingTab: "Billing",
    publicTitle: "Public profile",
    publicHint: "This information appears in app consent and the admin directory.",
    username: "Username",
    displayName: "Display name",
    phone: "Phone",
    avatar: "Avatar URL",
    gender: "Gender",
    genderUnset: "Not set",
    genderFemale: "Female",
    genderMale: "Male",
    genderUnspecified: "Prefer not to say",
    birthday: "Birthday",
    avatarUpload: "Upload avatar",
    avatarUploadOk: "Avatar uploaded",
    avatarUploadFailed: "Could not upload the avatar",
    avatarClear: "Remove uploaded avatar",
    avatarChoiceTitle: "Choose which avatar to use",
    avatarChoiceLead: "An avatar URL and an uploaded image both exist. Choose which one to show.",
    avatarChoiceUrl: "Use the URL",
    avatarChoiceUpload: "Use the uploaded image",
    avatarChoiceUploadHint: "The image stored on this platform",
    avatarChoiceFallback: "Use the other image if this one fails",
    avatarChoiceFallbackHint: "The other image appears only when the chosen one cannot be loaded.",
    avatarChoiceSave: "Use this image",
    avatarChoiceLater: "Decide later",
    saveProfile: "Save profile",
    profileSaved: "Profile saved",
    loadingProfile: "Loading profile",
    emailsTitle: "Email addresses",
    emailsHint: "The primary address is used for sign-in, security notices, and recovery.",
    addEmail: "Add email",
    addEmailTitle: "Add email",
    emailAddress: "Email address",
    addAndVerify: "Add and verify",
    emailAdded: "Email added. The verification message was sent.",
    codeSent: "The verification message was sent in the system template. Use the code in the message, or the button.",
    codePlaceholder: "Enter the code",
    confirmCode: "Confirm code",
    codeVerified: "Email verified",
    codeInvalid: "The code is wrong or expired",
    emailDeliveryFailed: "The address was saved, but the verification message was not sent. Use resend shortly.",
    addFailed: "Could not add",
    primaryEmail: "Primary",
    secondaryEmail: "Secondary",
    verified: "Verified",
    unverified: "Unverified",
    resend: "Resend verification",
    resendWait: "Wait {seconds} seconds before sending another verification email.",
    verificationSent: "Verification message sent",
    verificationFailed: "Could not send verification",
    emailsLoadFailed: "Unable to load email addresses",
    emailsEmpty: "No email address yet",
    removeEmail: "Remove email",
    cannotRemovePrimary: "Cannot remove the only primary email",
    mustAddAnother: "Add another email first",
    removeEmailConfirm: "Remove",
    removeEmailBody: "This address will no longer be used for notices or recovery.",
    emailRemoved: "Email removed",
    emailRemoveFailed: "Could not remove email",
    addressesTitle: "Address book",
    addressesHint: "Saved addresses are available to authorized apps. Country and postal code are checked on save.",
    addAddress: "Add address",
    editAddress: "Edit address",
    label: "Label",
    labelPlaceholder: "Home, office",
    country: "Country code",
    line1: "Address",
    line2: "Address line 2",
    city: "City",
    postalCode: "Postal code",
    region: "Region",
    defaultAddress: "Default",
    ordinaryAddress: "Other",
    saveAddress: "Save address",
    addressAdded: "Address added",
    addressUpdated: "Address updated",
    updateFailed: "Update failed",
    addressesLoadFailed: "Unable to load addresses",
    addressesEmpty: "No address yet",
    fallbackAddress: "Address",
    removeAddress: "Remove address",
    removeAddressBody: "Authorized apps will no longer be able to read this address.",
    addressRemoved: "Address removed",
    addressRemoveFailed: "Could not remove address",
    notifyTitle: "Security notices",
    notifyHint: "These switches only decide whether you are notified. They do not change tenant policy.",
    notifyLogin: "Unusual sign-in",
    notifyLoginHint: "Notify on a new location or device",
    notifyAdmin: "Admin actions",
    notifyAdminHint: "Notify when an admin resets your password, disables the account, or changes roles",
    billingTitle: "Billing & Payments",
    billingHint: "Payments are not enabled. OwO! Identity does not collect or store card numbers.",
    billingEmpty: "No payment method",
    billingEmptyHint: "When the tenant enables payments, bill methods can be managed here.",
    addPayment: "Add payment method",
  },
  dashboard: {
    greetingMorning: "Good morning",
    greetingAfternoon: "Good afternoon",
    greetingEvening: "Good evening",
    verifiedIdentity: "Verified identity",
    scope: "Access scope",
    scopePlatform: "Platform",
    scopeTenant: "Tenant admin",
    scopeMember: "Member",
    currentTenant: "Current tenant",
    sessionReady: "Signed in",
    sessionUnknown: "Checking session",
    eyebrow: "Identity overview",
    summary: "Access grants, sign-in protection, and recent activity live here.",
    securityCheck: "Security check",
    appsMetric: "Available apps",
    appsMetricDetail: "Current tenant catalog",
    consentsMetric: "Active grants",
    consentsMetricDetail: "Revoke any time",
    quickAccess: "Quick access",
    yourApps: "Your apps",
    viewAll: "View all",
    unnamedApp: "Untitled app",
    authorizedApp: "Authorized work app",
    noAuthorizedApps: "No authorized app has a homepage yet. Set it on the OIDC client, then sign in from that app.",
    openCatalog: "Open app catalog",
    protection: "Sign-in protection",
    securityStatus: "Security status",
    checklistEmail: "Verify primary email",
    checklistMfa: "Turn on a sign-in factor",
    checklistActivity: "Review recent sign-in activity",
    openSecurity: "Open security center",
    done: "Done",
    todo: "To do",
    layoutHint: "Drag a card by the handle. A grid marks where it can land, including the open space under a shorter card. The layout saves when you drop it.",
    layoutSaving: "Saving layout…",
    layoutSaved: "Overview layout saved",
    layoutFailed: "Could not save the overview layout",
    drag: "Drag to reorder",
    widgetEyebrow: "Overview card",
    platformTitle: "Platform status",
    platformFailed: "Could not read platform status",
    tenantCount: "Tenants",
    registeredUsers: "Registered users",
    registeredCount: "{count} registered",
    onlineCount: "{count} online",
    auditTitle: "Recent audit",
    tenantAudits: "Tenant audit",
    platformAudits: "Platform audit",
    auditFailed: "Could not read audit",
    auditEmpty: "No audit records yet",
    auditFailedEvent: "Did not succeed",
    globeTitle: "Where people are",
    globeFailed: "Could not read locations",
    globeUnavailable: "The local address database is unavailable, so the globe has no places yet.",
    globeEmpty: "No online session can be placed right now.",
    globeReset: "Reset view",
    unknownPlace: "Unnamed place",
  },
  query: {
    loadFailed: "Unable to load data",
    empty: "No records yet",
    refreshPage: "Refresh page",
    timeout: "The request timed out. Try again shortly.",
    network: "Network connection failed. Check your link and retry.",
    upstream: "Identity service is temporarily unavailable. Try again shortly.",
    csrf: "Security token expired. Refresh the page and try again.",
  },
  device: {
    title: "Authorize device sign-in",
    subtitle: "Enter the user code shown on the device, then approve to issue tokens.",
    userCode: "User code",
    lookup: "Look up request",
    client: "Client",
    status: "Status",
    expires: "Expires",
    flow: "Flow",
    flowDevice: "Device authorization",
    flowCiba: "Backchannel authentication (CIBA)",
    bindingMessage: "Binding message",
    loginHint: "Requested account",
    approve: "Approve",
    deny: "Deny",
    approved: "Sign-in approved",
    denied: "Sign-in denied",
    doneHint: "You can close this page and continue on the requesting device or service.",
    backHome: "Back to portal",
    missingFields: "Tenant slug and user code are required",
    verifyFailed: "Unable to verify the user code",
    actionFailed: "Action failed, please retry",
  },
  interact: {
    title: "Authorize application access",
    subtitle: "After you approve, the app receives a one-time authorization code for the scopes you grant.",
    heading: "Allow this app to access your identity?",
    intro: "Review the application name and requested scopes. Denying returns the app without tokens.",
    client: "Client",
    workspace: "Workspace",
    expires: "Request expires",
    scopes: "Requested scopes",
    approve: "Allow and continue",
    deny: "Deny",
    loading: "Loading the authorization request",
    missing: "This authorization request is missing. Restart sign-in from the application.",
    loadFailed: "Unable to load the authorization request",
    actionFailed: "Authorization failed, please retry",
    redirectFailed: "Unable to return to the application. Restart sign-in.",
  },
  apps: {
    eyebrow: "Access catalog",
    title: "Apps and consents",
    description: "Open tenant apps and decide which ones may keep access to your identity.",
    availableTitle: "Available apps",
    availableHint: "OIDC apps published by the current tenant. Open uses the homepage set on the client.",
    authorizedTitle: "Authorized apps",
    authorizedHint: "Revoke by scope. Tracked tokens are invalidated after revoke.",
    loadAppsFailed: "Unable to load the app catalog",
    noApps: "No apps available",
    noAppsAction: "Register an app in tenant admin",
    defaultDescription: "Sign in with OwO! Identity",
    openNew: "Open {name} in a new window",
    noLink: "{name} has no homepage yet",
    noConsents: "No authorized apps yet. They appear after you sign in to an app and grant consent.",
    openSecurity: "Open security center",
    lastUsed: "Last used",
    neverUsed: "Never used",
    highRisk: "High-risk scopes",
    revoke: "Revoke",
    revokeScope: "Revoke this scope",
    revokeAllTitle: "Revoke this app?",
    revokeAllBody: "Every granted scope and related tokens will stop working.",
    revokeScopeTitle: "Revoke this scope?",
    revokeScopeBody: "Only the selected scope is removed. Remaining scopes stay, and related tokens are invalidated.",
    revoked: "App consent revoked",
    scopeRevoked: "Scope revoked",
    revokeFailed: "Revoke failed",
    confirm: "Revoke",
    cancel: "Cancel",
  },
  notices: {
    tab: "Notices",
    title: "Security notices",
    hint: "Only events that already happened appear here. Unsubscribing stops that category from being delivered.",
    empty: "No security notices yet.",
    open: "Open notices",
    unread: "{count} unread notices",
    markRead: "Mark read",
    markAll: "Mark all read",
    unsubscribe: "Unsubscribe from this category",
    unsubscribed: "Unsubscribed from this category",
    unsubscribeFailed: "Could not unsubscribe",
    loadFailed: "Could not load notices",
    newDevice: "New device sign-in: {device}",
    passwordChanged: "Password changed",
    passwordReset: "Password was reset",
    passwordResetByAdmin: "An administrator reset your password",
    policyChanged: "Tenant security policy changed",
    developerSubmitted: "{applicant} applied to become a developer",
    developerApproved: "Your developer application was approved",
    developerRejected: "Your developer application was not approved",
    developerRejectedReason: "Your developer application was not approved: {reason}",
    developerRevoked: "Your developer access was removed",
    developerRevokedReason: "Your developer access was removed: {reason}",
    unknown: "Unknown device",
  },
  shell: {
    workspaceAdmin: "Managed workspace",
    workspaceCurrent: "Current workspace",
    noTenant: "No tenant selected",
    roleSuper: "Platform super admin · signed in at {tenant}",
    roleAdmin: "Tenant admin",
    roleMember: "Member",
    navLabel: "Primary",
    navPersonal: "Personal",
    navInbox: "Inbox",
    navBuild: "Build",
    navPayout: "Payouts",
    navManage: "Manage",
    closeNav: "Close navigation",
    openNav: "Open navigation",
    scrollBack: "Scroll left",
    scrollForward: "Scroll right",
    commandPalette: "Command palette",
    commandHint: "Command palette (Ctrl/⌘ K)",
    notifications: "Notices",
    notificationsEmpty: "Unusual sign-ins and admin actions are summarized here. Switches are stored with your preferences.",
    manageNotifications: "Manage notices",
    memberFallback: "Member",
    workspaceFallback: "Workspace",
    skip: "Skip to main content",
    profileMenu: "Account menu",
    quickActions: "Quick actions",
  },
  users: {
    selfDisableHint: "You cannot disable or ban your own account",
    roleGroups: "Permission groups",
    roleGroupsHint: "This changes only directly granted groups. Roles inherited from a user group stay on the member list.",
    roleGroupsSelf: "You cannot change your own permission groups.",
    roleGroupsSaved: "Profile and permission groups saved",
    roleGroupsRemoveTitle: "Remove these permission groups?",
    roleGroupsRemoveHint: "Direct grants to remove: {roles}. Access changes immediately and is written to the audit log.",
    roleGroupsRemoveOk: "Remove groups",
    superAdmin: "Super administrator",
    protectedHint: "Only super administrators can manage this account",
    protectedRelationsHint: "This role or group includes a super administrator; only super administrators can modify it",
    eyebrow: "Directory",
    title: "Users and invitations",
    description: "Create, invite, disable, or permanently remove members. Bulk actions change who can sign in.",
    searchPlaceholder: "Search name, email, role, or group",
    statusPlaceholder: "Account status",
    statusActive: "Active",
    statusInactive: "Disabled",
    statusBanned: "Banned",
    bulkDeactivate: "Disable selected",
    bulkNeedSelection: "Select active accounts to disable",
    bulkTitle: "Disable {count} selected accounts?",
    bulkConsequence: "These {count} accounts will be unable to sign in. Last-admin accounts are skipped and listed as failures.",
    bulkOk: "Disable selected",
    bulkDone: "Disabled {succeeded}, failed {failed}",
    invite: "Invite member",
    create: "Create user",
    loadFailed: "User admin API unavailable",
    empty: "No users yet",
    noMatch: "No users match the current search or status",
    selectUser: "Select {name}",
    colUser: "User",
    colRoles: "Roles",
    colGroups: "Groups",
    colStatus: "Status",
    colOnline: "Online",
    colLastLogin: "Last sign-in",
    colLastIP: "Last sign-in IP",
    online: "Online",
    offline: "Offline",
    moreRoles: "+{count} groups",
    colActions: "Actions",
    mfaOn: "Enabled",
    mfaOff: "Off",
    edit: "Edit",
    resetPassword: "Reset password",
    deactivate: "Disable",
    deactivateTitle: "Disable {name}?",
    deactivateConsequence: "This account will be unable to sign in.",
    deleteForever: "Delete permanently",
    invitesTitle: "Pending invitations",
    invitesHint: "One-time links last 7 days and can assign member or tenant admin.",
    invitesLoadFailed: "Unable to load invitations",
    invitesEmpty: "No pending invitations.",
    roleAdmin: "Tenant admin",
    roleSuper: "Super admin",
    roleMember: "Member",
    noRole: "None assigned",
    noGroup: "No group",
    inviteExpires: "Expires {time}",
    invitePending: "Pending",
    revokeInvite: "Revoke",
    revokeInviteTitle: "Revoke invitation?",
    revokeInviteConsequence: "The link stops working immediately.",
    updated: "User profile updated",
    created: "User created in {tenant}",
    updateFailed: "Update failed",
    createFailed: "Create failed",
    inviteCreated: "Invitation created",
    inviteLinkHint: "Copy the link for the invitee. Email delivery depends on SMTP.",
    inviteLink: "Invitation link",
    inviteFailed: "Invitation failed",
    inviteRevoked: "Invitation revoked",
    revokeFailed: "Revoke failed",
    passwordReset: "Password reset. Existing sessions ended.",
    passwordResetFailed: "Password reset failed",
    deactivated: "Account disabled",
    deactivateFailed: "Disable failed",
    deleted: "User data permanently deleted",
    deleteFailed: "Permanent delete failed",
    editTitle: "Edit {name}",
    createTitle: "Create a user in {tenant}",
    username: "Username",
    usernameRule: "Enter a username of at least 3 characters",
    email: "Email",
    emailRule: "Enter a valid email",
    fullName: "Name",
    phone: "Phone",
    avatar: "Avatar URL",
    accountEnabled: "Account enabled",
    accountBanned: "Account banned",
    banReason: "Ban reason",
    initialPassword: "Initial password",
    passwordPolicyHint: "Must satisfy the tenant password policy.",
    passwordRule: "Password must be at least 8 characters",
    saveProfile: "Save user profile",
    inviteTitle: "Invite a member to {tenant}",
    role: "Role",
    sendInvite: "Send invitation",
    resetTitle: "Reset password for {name}",
    resetWarning: "Existing sessions end as soon as this is saved",
    resetHint: "The new password must satisfy the tenant policy. Deliver it through a safe channel.",
    newPassword: "New password",
    confirmPassword: "Confirm new password",
    confirmPasswordRule: "Enter the new password again",
    passwordMismatch: "The two passwords do not match",
    deleteTitle: "Delete user permanently",
    deleteWarning: "This cannot be undone",
    deleteHint: "The account, sessions, tokens, MFA credentials, grants, linked identities, and profile are removed.",
    deleteConfirm: "Type the username {name} to confirm:",
  },
  protocol: {
    oidc: "OIDC Discovery",
    jwks: "JSON Web Key Set",
    saml: "SAML IdP Metadata",
    scim: "SCIM ServiceProviderConfig",
    refresh: "Refresh",
    loadFailed: "Unable to read {name}",
    scimHint: "This needs the current tenant admin session. Confirm you can access this workspace, then retry.",
    genericHint: "The identity service did not return readable content. Retry shortly, or check that the protocol is enabled.",
  },
  platform: {
    domainsTitle: "Trusted domains",
    domainsHint: "Set the platform portal host and the origins the API accepts.",
    domainsLoadFailed: "Domain settings are unavailable",
    adminHost: "Platform admin host",
    origins: "Allowed origins (one per line)",
    restartCors: "Restart the identity service after saving so the CORS boundary updates",
    saveDomains: "Verify and save",
    domainsSaved: "Domain boundary saved",
    domainsRestart: "Domain boundary saved. Restart the identity service to apply it.",
    saveFailed: "Save failed",
    runtimeTitle: "Runtime configuration",
    runtimeHint: "Secrets stay masked. Leave a field blank to keep the current value. Database and Redis are tested before save.",
    runtimeLoadFailed: "Runtime settings are unavailable",
    externalUrl: "External service URL",
    databaseDsn: "PostgreSQL DSN{configured}",
    configured: " (configured)",
    keepBlank: "Leave blank to keep the current value",
    redisAddr: "Redis address",
    redisDb: "Redis DB",
    redisPassword: "Redis password{configured}",
    smtpHost: "SMTP host",
    smtpPort: "SMTP port",
    smtpUser: "SMTP username",
    smtpPassword: "SMTP password{configured}",
    smtpFrom: "From",
    smtpTls: "Enable SMTP TLS",
    restartRuntime: "Restart the identity service after saving",
    saveRuntime: "Verify and save",
    runtimeSaved: "Runtime configuration saved",
    runtimeRestart: "Runtime configuration saved. Restart the identity service.",
    reauthTitle: "Confirm it is you",
    reauthHint: "Enter your current password before saving domains or runtime configuration. It is checked once and is not stored in the configuration.",
    currentPassword: "Current password",
    reauthFailed: "Password did not match. Configuration was not saved.",
    reauthConfirm: "Check and continue",
    eyebrow: "Platform console",
    title: "OwO! Identity control plane",
    description: "Manage every tenant, runtime setting, and service health. Sensitive changes are audited and say when a restart is required.",
    tabTenants: "Tenants",
    tabDossier: "User dossiers",
    tabAdmins: "System administration",
    tabCompliance: "Compliance",
    tabHealth: "Health",
    tabDomains: "Domain boundary",
    tabRuntime: "Runtime",
    tabMail: "Mail templates",
    tabNotices: "Inbox",
    noticePolicyEyebrow: "Notices",
    noticePolicyTitle: "Inbox events",
    noticePolicyHint: "These switches apply to the whole platform. An enabled event is written to the inbox. A disabled event is not delivered. New-device sign-in, password changes, security policy, and developer applications start enabled. People can still unsubscribe from sign-in and administration notices.",
    noticePolicySearch: "Search events or actions",
    noticePolicySaved: "Inbox switches saved",
    tabPayment: "Payment",
    tabWithdrawal: "Withdrawals",
    noticePolicyFailed: "Could not save inbox switches",
    noticePolicyLoadFailed: "Could not load inbox switches",
    noticePolicyEmpty: "No matching events",
    noticeAudienceSelf: "To the person",
    noticeAudienceTenant: "To tenant administrators",
    noticeAudiencePlatform: "To super administrators",
    tabOps: "Queues and logs",
    tabLoad: "Load",
    tabAssets: "Assets",
    smtpProbe: "Test SMTP",
    smtpProbeTitle: "Test the running SMTP service",
    smtpProbeHint: "This checks the service that is running now, not unsaved form values. Leave the recipient empty to test the connection and login without sending mail.",
    smtpProbeTo: "Test recipient, optional",
    smtpProbeOk: "SMTP connection and login succeeded",
    smtpProbeSent: "SMTP test message was sent",
    smtpProbeFailed: "SMTP test failed",
    opsEyebrow: "Operations",
    opsTitle: "Queues and audit",
    opsHint: "The queue shows work waiting to be delivered. Platform logs and audit stay separate from each tenant audit.",
    opsRefresh: "Refresh",
    queueDead: "Dead",
    queuePending: "Pending",
    queueProcessing: "Processing",
    queueDone: "Done",
    queueLoadFailed: "Could not read the queue",
    queueEmpty: "No tasks in this state",
    opsWhen: "Time",
    opsEvent: "Event",
    opsReason: "Reason",
    opsStatus: "Status",
    opsAttempts: "Attempts",
    opsError: "Error",
    opsViewOps: "Operations",
    opsViewAttention: "Needs attention",
    opsViewPlatform: "Platform",
    opsViewTenant: "Tenant",
    opsLogEyebrow: "Tenant",
    opsLogTitle: "Tenant audit",
    opsLogHint: "Each record names its tenant, OIDC application, and user. Filter by tenant, event, resource, IP, or trace.",
    opsSearch: "Search resource, IP, or trace",
    opsAction: "Event name, exact",
    opsOutcome: "Outcome",
    opsAnyOutcome: "Any outcome",
    opsFailures: "Failures only",
    opsSuccesses: "Successes only",
    opsResource: "Resource",
    opsTrace: "Trace",
    opsLogLoadFailed: "Could not read audit logs",
    opsLogEmpty: "No matching logs",
    opsDetailTruncated: "The detail is too long and was omitted. Export the tenant audit log for the full record.",
    opsFailureEyebrow: "Needs attention",
    opsFailureTitle: "Failures and errors",
    opsFailureHint: "Switch between failures, warnings, and errors. Failures are unsuccessful platform and tenant audits. Warnings and errors are runtime logs at that level.",
    opsSeverityFailure: "Failure",
    opsSeverityWarning: "Warning",
    opsSeverityError: "Error",
    opsFailureAll: "All",
    opsFailureLogs: "Runtime logs",
    opsFailurePlatformAudit: "Platform audit",
    opsFailureTenantAudit: "Tenant audit",
    opsFailureKind: "Type",
    opsFailureSearch: "Search failures, trace, or IP",
    opsFailureEmpty: "No failures or errors need attention",
    opsFailureLoadFailed: "Could not read failure records",
    opsPlatformEyebrow: "Platform",
    opsPlatformTitle: "The platform itself",
    opsPlatformHint: "Read records the identity service produced. They are not mixed into one tenant.",
    opsPlatformLogTitle: "Runtime logs",
    opsPlatformLogHint: "Console requests, startup, SMTP probes, and domain or runtime changes. Health probes and metric scrapes are left out.",
    opsPlatformAuditTitle: "Platform audit",
    opsPlatformAuditHint: "Signing keys, super administrators, mail templates, and platform settings. Expand a row to read the record.",
    opsTenantFilter: "Tenant",
    opsAllTenants: "All tenants",
    opsOrigin: "Origin",
    opsApp: "OIDC application",
    opsUser: "User",
    opsLevel: "Level",
    opsSource: "Source",
    opsMessage: "Record",
    opsAnyLevel: "Any level",
    opsAnySource: "Any source",
    opsPlatformScope: "Platform",
    opsTenantMissing: "Tenant not found",
    opsClientUnnamed: "Name not registered",
    opsNoApp: "No linked application",
    opsNoUser: "No linked user",
    opsUserMissing: "User not found",
    opsUserCard: "View details for {name}",
    opsEmail: "Email",
    opsGroups: "Groups",
    opsPermissions: "Permissions",
    opsLastLogin: "Last sign-in",
    opsRegistered: "Registered",
    opsNever: "Not signed in yet",
    opsNoGroups: "No groups",
    opsNoPermissions: "No permissions",
    opsActor: "Operator",
    opsPlatformLogEmpty: "No platform runtime logs yet",
    opsPlatformAuditEmpty: "No platform audit yet",
    opsPlatformLogLoadFailed: "Could not read platform runtime logs",
    opsPlatformAuditLoadFailed: "Could not read platform audit",
    opsLogSearch: "Search record, trace, or IP",
    mailEyebrow: "SMTP",
    mailTitle: "Mail templates",
    mailHint: "Messages use the built-in template until you switch on a custom body. Placeholders are replaced at send time; scripts and unknown fields are rejected. Announcement levels can be enabled independently and sent to active users with verified email in their audience.",
    mailSearch: "Search events or templates",
    mailSystem: "System messages",
    mailEvents: "Event triggers",
    mailUseCustom: "Use a custom template",
    mailUseCustomHint: "When this is off, the built-in template is sent.",
    mailEnable: "Send when this event happens",
    mailEnableHint: "Off by default. When on, the user on the event receives this message.",
    mailSubject: "Subject",
    mailBody: "Body",
    mailFields: "Reserved fields",
    mailFieldsHint: "Write a field such as {username} in the body. It is replaced when the message is sent.",
    mailPreview: "Preview this body",
    mailPreviewSystem: "Preview system template",
    mailSave: "Save template",
    mailSaved: "Template saved",
    mailSaveFailed: "Could not save the template",
    mailReset: "Restore system template",
    mailResetTitle: "Restore the system template?",
    mailResetHint: "The saved custom body is deleted. Later messages use the system template again.",
    mailResetOk: "Restore system template",
    mailResetDone: "System template restored",
    mailLoadFailed: "Could not load mail templates",
    mailEmpty: "No matching event",
    mailInlineHint: "An account flow sends this message. The system template is used while custom is off.",
    mailBusHint: "This comes from the event manager. It is sent only when “Send when this event happens” is on.",
    mailSubjectRequired: "Enter a subject",
    mailBodyRequired: "Enter a body",
    mailEventVerification: "Email verification",
    mailEventReset: "Password reset",
    mailEventInvitation: "Invitation",
    mailEventSecurity: "Security alert",
    mailEventTemplate: "Template updated",
    mailEventBillCreated: "Bill created",
    mailEventBillPaid: "Bill paid",
    mailEventWithdrawalSubmitted: "Withdrawal submitted",
    mailEventWithdrawalApproved: "Withdrawal approved",
    mailEventWithdrawalRejected: "Withdrawal rejected",
    mailEventWithdrawalProcessed: "Withdrawal processed",
    mailEventAnnouncementInfo: "Info announcements",
    mailEventAnnouncementWarning: "Warning announcements",
    mailEventAnnouncementCritical: "Critical announcements",
    mailCustomMark: "Custom",
    mailEnabledMark: "On",
    adminsEyebrow: "Platform permission group",
    adminsTitle: "System administration",
    adminsHint: "Super Administrator is a platform permission group. Grant or revoke it only here. Tenants cannot create or assign it.",
    groupScope: "Platform",
    groupStamp: "System",
    groupReadonly: "The platform catalog owns these permissions. Tenant roles cannot select them.",
    groupPerms: "Permissions in system administration",
    groupEmpty: "The catalog has no platform permissions yet. They appear after the database migration.",
    groupLoadFailed: "Unable to read platform permission groups",
    membersEyebrow: "Members",
    membersTitle: "Group members",
    membersHint: "A granted user can open the platform console immediately. At least one usable super admin must remain.",
    userUuid: "User UUID",
    pickUser: "Username or email",
    pickEmpty: "No matching people",
    alreadyAdmin: "Already a platform admin",
    selectUser: "Select {name}",
    revokeLast: "At least one platform admin must remain",
    revokeFailed: "Could not revoke access",
    grant: "Grant",
    grantTitle: "Grant super administrator",
    grantHint: "Enter the step-up password before {name} receives the platform console.",
    granted: "Super admin granted",
    grantFailed: "Grant failed",
    revokeTitle: "Revoke super admin?",
    revokeHint: "This user loses platform access immediately. The last usable super admin cannot be revoked.",
    revokeOk: "Revoke access",
    revoked: "Access revoked",
    adminsLoadFailed: "Unable to read super admins",
    adminsEmpty: "No super admins granted yet",
    user: "User",
    status: "Status",
    actions: "Actions",
    inactive: "Inactive",
    available: "Active",
    revoke: "Revoke",
    tenantsEyebrow: "Tenant catalog",
    tenantsTitle: "Platform tenants",
    tenantsHint: "Open a workspace to manage members, protocols, and access without changing your sign-in.",
    createTenant: "Create tenant",
    tenantsLoadFailed: "Unable to read tenants",
    tenantsEmpty: "No tenants yet",
    tenant: "Tenant",
    running: "Running",
    stopped: "Disabled",
    createdAt: "Created",
    manage: "Manage",
    editProfile: "Edit profile",
    disableTitle: "Disable {name}?",
    disableHint: "Users and apps in this tenant lose access until it is enabled again.",
    disableOk: "Disable tenant",
    disable: "Disable",
    purge: "Remove permanently",
    editTenant: "Manage tenant",
    slug: "Tenant slug",
    internalName: "Internal name",
    displayName: "Display name",
    enableTenant: "Enable tenant",
    registration: "Allow self-service registration",
    registrationExtra: "When off, the public register endpoint and login sign-up form are blocked. Invitations still work.",
    registrationColumn: "Self-service sign-up",
    registrationOpen: "Open",
    registrationClosed: "Closed",
    registrationToggle: "Toggle self-service sign-up for {name}",
    registrationOn: "Self-service sign-up is open",
    registrationOff: "Self-service sign-up is closed",
    registrationOffTitle: "Close self-service sign-up for {name}?",
    registrationOffHint: "The public sign-up form and register API stop accepting new accounts immediately. Existing sign-in and invitations keep working.",
    registrationOffOk: "Close sign-up",
    registrationFailed: "Could not update sign-up permission",
    forcePar: "Require pushed authorization",
    forceParExtra: "When on, every authorization request in this tenant must push parameters first.",
    risk: "Enable sign-in risk step-up",
    riskExtra: "When off, new locations and devices no longer force MFA.",
    newIp: "Force MFA on a new IP",
    newDevice: "Force MFA on a new device",
    stepScore: "Minimum step-up score",
    stepScoreExtra: "Default 50. At this score, enrolled MFA is required.",
    archiveNote: "Cold archive follows server retention.archive_*",
    archiveHint: "When enabled, expired audit, sessions, and invites are written as JSONL (fs or S3) before purge. See the backend audit-cold-archive runbook.",
    auditDays: "Audit retention (days)",
    auditDaysExtra: "0 keeps records forever. Maximum 3650.",
    sessionDays: "Session retention (days)",
    inviteDays: "Invitation retention (days)",
    deletedDays: "Deleted-user retention (days)",
    saveTenant: "Save tenant",
    tenantUpdated: "Tenant updated",
    tenantCreated: "Tenant created",
    tenantDisabled: "Tenant disabled",
    disableFailed: "Disable failed",
    purgeTitle: "Remove tenant permanently",
    purgeOk: "Delete permanently",
    irreversible: "This cannot be undone",
    purgeHint: "Users, credentials, sessions, apps, grants, SAML configuration, and audit data are physically deleted.",
    purgeLead: "Type tenant slug ",
    purgeTail: " to confirm:",
    tenantPurged: "The tenant and all of its data were permanently deleted",
    purgeFailed: "Permanent delete failed",
    complianceEyebrow: "Compliance posture",
    complianceTitle: "Retention and session health",
    complianceHint: "Hot-store size and cold-archive batches per tenant. Filter sessions by issuing region. Force re-login signs those sessions out immediately.",
    refreshPosture: "Refresh posture",
    regionRequired: "Enter an issuing region first",
    forceTitle: "Force re-login for region “{region}”?",
    forceHint: "Matching active sessions in this region stop immediately: refresh tokens are revoked and access tokens are denied. People must sign in again.",
    forceOk: "Force re-login",
    forced: "Forced re-login for {count} sessions",
    forceFailed: "Force re-login failed",
    postureWatch: "Needs attention",
    postureCalm: "Steady",
    postureNow: "Current posture: {tone}",
    archiveFailedLine: "{count} archive batches failed. Hot-store rows may not have been cleared on time.",
    archiveClear: "No failed archive batches.",
    outboxDead: "{count} dead outbox messages. Check delivery.",
    outboxClear: "No dead outbox messages.",
    noBatches: "Cold archive has not produced a batch yet",
    noBatchesHint: "Hot-store data still follows each tenant's retention days. To archive before purge, enable retention.archive_enabled (fs or s3) on the server. Notes are on the runtime page.",
    openRuntime: "Open runtime configuration",
    archiveFailed: "Failed archive batches",
    outboxDeadTitle: "Dead outbox",
    generatedAt: "Generated",
    complianceLoadFailed: "Unable to read the compliance overview",
    complianceEmpty: "No tenant posture to show yet. Create or enable a workspace first.",
    openTenants: "Open tenant catalog",
    auditRows: "Audit rows",
    sessions: "Sessions",
    invitations: "Invitations",
    archiveDone: "Archive succeeded",
    archiveFailCol: "Archive failed",
    latestArchive: "Latest archive",
    regionEyebrow: "Session region",
    regionTitle: "Inspect by issuing region",
    regionHint: "Issuing region is the label written when a session is created. Force re-login revokes refresh tokens and denies access tokens.",
    regionPlaceholder: "Issuing region, for example ap-east-1",
    regionLabel: "Issuing region",
    forceRegion: "Force re-login in this region",
    sessionsLoadFailed: "Unable to read sessions",
    sessionsEmpty: "No matching active sessions. Try another issuing region, or check retention notes on the runtime page.",
    viewRuntime: "View runtime notes",
    unmarked: "Unmarked",
    client: "Client",
    created: "Created",
    http: "HTTP service",
    healthy: "Healthy",
    down: "Down",
    stores: "PostgreSQL / Redis",
    ready: "Ready",
    notReady: "Unavailable",
    startup: "Startup check",
    checking: "Checking",
    metrics: "Metrics",
    metricsOk: "Available",
    refresh: "Refresh",
    metricsLoading: "Reading metrics...",
  },
  login: {
    thesis: "One key, only the doors that should open.",
    thesisBody: "OwO! Identity keeps sign-in, consent, and security state on one control plane: confirm who you are, then decide where you can go.",
    isolation: "Tenant isolation",
    isolationHint: "Every visit is bound to a workspace",
    mfa: "Multi-factor authentication",
    mfaHint: "Codes, backup codes, and security keys",
    eyebrow: "Secure sign-in",
    title: "Enter OwO! Identity",
    intro: "Continue with your workspace and account.",
    signedOut: "This device was signed out from another session. Sign in again.",
    workspace: "Workspace",
    workspaceRequired: "Enter a workspace",
    workspacePlaceholder: "For example master or acme",
    workspaceFixedLead: "This application set the workspace to ",
    workspaceFixedTail: ". Sign-in, registration, and password reset stay in this workspace.",
    login: "Username or email",
    loginRequired: "Enter a username or email",
    password: "Password",
    passwordRequired: "Enter a password",
    submit: "Sign in",
    forgot: "Forgot password",
    registerLink: "Create an account",
    registerTitle: "Create an account",
    registerIntro: "Self-service sign-up for this workspace.",
    registerSubmit: "Register",
    registerSuccess: "Account created. Verify the email before signing in.",
    registerFailed: "Registration failed",
    registerMailFailed: "The verification email was not sent, so the account was not created. Submit it again later.",
    registerExists: "That username or email already has an account. Sign in, or choose another one.",
    verifyEmailEyebrow: "Email verification",
    verifyEmailTitle: "Verify your email",
    verifyEmailIntro: "A verification code was sent to {email}. Sign-in stays closed until the address is verified.",
    verifyEmailIntroOidc: "A verification code was sent to {email}. After it is verified, sign in to continue to the application.",
    verifyEmailCode: "Verification code",
    verifyEmailCodeRequired: "Enter the verification code",
    verifyEmailSubmit: "Verify email",
    verifyEmailResend: "Send another code",
    verifyEmailResendWait: "Send another code in {seconds}s",
    verifyEmailResent: "A new code was sent to {email}.",
    verifyEmailResendFailed: "The code could not be sent again.",
    verifyEmailInvalid: "That code is invalid or expired. Enter it again, or send a new one.",
    verifyEmailDeliveryFailed: "The verification code was not sent. Send it again, then enter it.",
    verifyEmailDoneTitle: "Email verified",
    verifyEmailDone: "You can sign in now.",
    verifyEmailDoneOidc: "You can sign in, then continue to the application.",
    verifyEmailSignIn: "Go to sign-in",
    registerDisabled: "Self-service registration is turned off for this workspace.",
    registerUsername: "Username",
    registerUsernameRequired: "Enter a username",
    registerEmail: "Email",
    registerEmailRequired: "Enter an email",
    registerFullName: "Display name",
    registerPassword: "Password",
    registerPasswordRequired: "Enter a password",
    backToSignIn: "Back to sign-in",
    protected: "Protected identity service",
    back: "Back to password",
    stepEyebrow: "Second step",
    confirmTitle: "Confirm it is you",
    confirmLead: "Password accepted. Finish the security check required by ",
    confirmTail: ".",
    verify: "Verify and sign in",
    expires: "This check expires shortly",
    totp: "Authenticator",
    totpShort: "Code",
    totpHint: "Enter the 6-digit code from your authenticator.",
    backup: "Backup code",
    backupShort: "Backup",
    backupHint: "Enter an unused backup code. It stops working after this check.",
    webauthn: "Security key",
    webauthnShort: "Key",
    webauthnHint: "Use a passkey on this device or a registered hardware key.",
    useKey: "Verify with security key",
    backupPlaceholder: "Enter a backup code",
    noMethod: "This account requires a second step, but no method is available",
    signInFailed: "Unable to sign in. Check the service connection.",
    invalidCredentials: "Username or password is incorrect. Try again, or reset the password.",
    accountLocked: "This account is temporarily locked. Try again later, or reset the password.",
    accountBanned: "This account is disabled. Contact an administrator.",
    accountUnverified: "Email is not verified yet. Finish the verification email, then sign in.",
    loginDisabled: "Password sign-in is turned off for this workspace.",
    enterCode: "Enter {method}",
    verifyFailed: "Verification failed. Try again.",
    keyUnsupported: "This browser does not support security keys",
    keyCancelled: "Security key verification was cancelled",
    keyFailed: "Security key verification failed",
    forgotTitle: "Reset password",
    forgotOk: "Send reset email",
    forgotBody: "Enter the workspace and account email. If the account exists, we send a one-time reset link.",
    forgotBodyFixed: "The reset email goes only to workspace {workspace}. If the account exists, the link arrives shortly.",
    forgotWorkspace: "Workspace, for example master",
    forgotEmailMissing: "Enter an email address",
    forgotEmail: "Account email",
    requirementsTitle: "This workspace requires",
    lockoutHint: "After {attempts} failed attempts, the account locks for {minutes} minutes.",
    mfaRequired: "Sign-in requires a second step.",
    mfaOptional: "A second step depends on this account's own security settings.",
    passwordDisabled: "Password sign-in is turned off for this workspace.",
    federationHint: "Enabled identity providers: {names}. After password sign-in, link one in Security.",
    continueWith: "Continue with {name}",
    realmMatch: "{domain} signs in with {name}.",
    realmAmbiguous: "{domain} matches more than one identity provider. Choose one.",
    usePasswordInstead: "Use a password instead",
    passkey: "Sign in with a passkey",
    passkeyMissing: "This account has no passkey that can sign in. Use a password, or register one in Security.",
    passkeyInvalid: "Passkey verification failed. Try again, or use a password.",
    passkeyFailed: "Passkey sign-in could not continue.",
    passkeyRecovery: "If the passkey is lost, reset the password to enter the account, then register a new passkey in Security.",
    federationFailed: "That identity provider could not sign you in. Use a password, or contact an administrator.",
    federationNotLinked: "This external account is not linked yet. Sign in with a password, then link it in Security.",
    federationDisabled: "This workspace has not opened that identity provider for sign-in.",
    providerGoogle: "Google",
    providerGitHub: "GitHub",
    providerMicrosoft: "Microsoft",
    providerApple: "Apple",
    providerOkta: "Okta",
    providerOidc: "Enterprise OIDC",
    providerLdap: "Directory",
    forgotMissing: "Enter the workspace and email",
    forgotSent: "If the account exists, the reset email will arrive shortly",
    forgotFailed: "Unable to submit the reset request",
  },
  security: {
    eyebrow: "Sign-in protection",
    title: "Security center",
    description: "Manage your password, factors, devices, and linked identities.",
    sessionsTab: "Sign-in activity",
    logsTab: "Sign-in log",
    mfaTab: "Multi-factor",
    linksTab: "Linked accounts",
    passwordTab: "Change password",
    devicesTitle: "Devices and history",
    devicesHint: "Names come from the browser, operating system, and device model. Sign out an active session on its own. Signed-out history can be deleted.",
    signOutOthers: "Sign out other devices",
    sessionsLoadFailed: "Sign-in history is unavailable",
    sessionsEmpty: "No sign-in activity yet.",
    selectHistory: "Select signed-out records on this page",
    deleteHistory: "Delete history",
    selectDevice: "Select {name}",
    unknownDevice: "Unknown device",
    unknownIp: "Unknown IP",
    lastActive: "Last active {time}",
    scope: "Scope {scope}",
    currentDevice: "This device",
    active: "Active",
    signedOut: "Signed out",
    displaced: "Displaced by a new sign-in",
    signOutDevice: "Sign out device",
    deleteOne: "Delete this sign-in activity",
    signOutOthersTitle: "Sign out other devices?",
    signOutConfirm: "Sign out",
    signOutOthersAlert: "Every signed-in device except this browser will be signed out",
    signOutOthersHint: "This browser stays signed in. Other sessions stop immediately.",
    signOutOneTitle: "Sign out this device?",
    signOutOneAlert: "That device's refresh session stops immediately",
    signOutOneHint: "This browser is not affected.",
    deleteManyTitle: "Delete {count} history records?",
    deleteOneTitle: "Delete this sign-in activity?",
    deleteAlert: "History is permanently deleted",
    deleteHint: "Only signed-out records can be deleted. This cannot be undone.",
    deleted: "Deleted {count} sign-in records",
    deleteFailed: "Could not delete sign-in activity",
    deviceSignedOut: "That device is signed out",
    revokeFailed: "Could not revoke the session",
    othersSignedOut: "Signed out {count} other devices",
    noOtherDevices: "No other devices are signed in",
    othersFailed: "Could not sign out other devices",
    logsTitle: "Recent sign-in log",
    logsHint: "The latest 100 local and federated sign-ins. Failures use a red signal, and the result matches the action name.",
    logsLoadFailed: "Sign-in log is unavailable",
    logsEmpty: "No sign-in log yet",
    logScope: "Scope {scope}",
    methodsTitle: "Verification methods",
    methodsHint: "Register credentials and choose which ones sign-in requires.",
    methodsLoadFailed: "Unable to read sign-in verification settings",
    authenticator: "Authenticator",
    totpOn: "Configured. Sign-in requires a code.",
    totpOff: "Configured. Sign-in verification is paused.",
    totpNone: "Use a TOTP authenticator app",
    loginToggle: "Require at sign-in",
    totpSwitch: "Use authenticator at sign-in",
    passkeySwitch: "Use passkey at sign-in",
    disable: "Disable",
    setup: "Set up",
    passkeyTitle: "Security key / passkey",
    passkeyOn: "Configured. Sign-in can use a passkey.",
    passkeyOff: "Configured. Sign-in verification is paused.",
    passkeyNone: "Register this device or a hardware key with WebAuthn",
    registerKey: "Register a key",
    backupTitle: "Backup codes",
    backupRemaining: "{count} left. Each code works once.",
    regenerate: "Regenerate",
    disableTitle: "Disable authenticator?",
    disableOk: "Disable and delete",
    disableAlert: "Codes will no longer work for sign-in",
    disableHint: "This deletes the current TOTP secret. To pause sign-in verification only, turn off “Require at sign-in”.",
    setupTitle: "Set up authenticator",
    setupOk: "Verify and enable",
    setupScan: "Scan the QR code, or enter the secret manually:",
    codeLabel: "6-digit code",
    codePlaceholder: "Enter the 6-digit code",
    replaceTitle: "Replace existing backup codes?",
    replaceOk: "Replace and generate",
    replaceAlert: "All previous backup codes stop working",
    replaceHint: "Be ready to store the new codes. Old codes are not shown again.",
    newCodes: "New backup codes",
    codesSaved: "I stored them safely",
    codesOnce: "These codes are shown once",
    codesHint: "Each code works once. Store them before closing this window.",
    copyCodes: "Copy all codes",
    setupIncomplete: "Authenticator setup response was incomplete",
    setupFailed: "Could not start authenticator setup",
    codeRequired: "Enter the 6-digit code from the authenticator",
    totpEnabled: "Authenticator enabled",
    codeInvalid: "Code was not accepted. Authenticator is not enabled.",
    totpDisabled: "Authenticator disabled",
    disableFailed: "Disable failed",
    loginOn: "Sign-in verification turned on",
    loginOff: "Sign-in verification turned off. Registered credentials stay.",
    loginUpdateFailed: "Could not update sign-in verification",
    passkeyUnsupported: "This browser does not support passkeys",
    passkeyCancelled: "Passkey registration was cancelled",
    passkeyRegistered: "Passkey registered",
    passkeyFailed: "Passkey registration failed",
    codesIncomplete: "Backup code response was incomplete",
    codesFailed: "Could not generate codes",
    codesCopied: "Backup codes copied",
    clipboardFailed: "Clipboard is unavailable. Store the codes manually.",
    linksTitle: "External identities",
    linksHint: "Link a directory or a developer account.",
    ldap: "Enterprise LDAP",
    popupBlocked: "The browser blocked the authorization window. Allow pop-ups for this site.",
    linked: "{name} linked",
    linkTitle: "Link {name}",
    ldapUser: "LDAP username",
    ldapPassword: "LDAP password",
    linkOk: "Link account",
    unlinked: "Link removed",
    unlinkFailed: "Could not remove the link",
    linkedAs: "Linked",
    providerOff: "This tenant has not enabled this provider",
    notLinked: "Not linked",
    unlinkTitle: "Remove the {name} link?",
    unlink: "Remove",
    link: "Link",
    passwordTitle: "Change password",
    passwordHint: "The new password must satisfy the tenant policy.",
    reviewActivity: "I have reviewed this sign-in activity",
    reviewActivitySaved: "Review time saved",
    reviewActivityHint: "After you confirm, the overview item stays complete for 30 days.",
    reviewActivityFailed: "Could not save the review time",
    currentPassword: "Current password",
    newPassword: "New password",
    savePassword: "Save new password",
    passwordSaved: "Password changed. Other sessions ended.",
    passwordFailed: "Change failed",
  },
  adminNav: {
    eyebrow: "Tenant console",
    title: "Manage {tenant}",
    acting: "Managing {tenant} as platform admin. Your sign-in still belongs to {home}.",
    description: "Manage members, protocol clients, access boundaries, and audit records.",
    overview: "Overview",
    users: "Users",
    roles: "Roles",
    groups: "Groups",
    reviews: "Access reviews",
    federation: "Federation",
    audit: "Audit log",
    ip: "IP rules",
    oidc: "OIDC apps",
    saml: "SAML services",
    protocol: "Protocol diagnostics",
    policy: "Security policy",
    developers: "Developer requests",
    activeToday: "Active users today",
    newWeek: "New in 7 days",
    failedHour: "Failed sign-ins, 1 hour",
    sessions: "Active sessions",
    mfaRate: "MFA adoption",
  },
  passwordRules: {
    title: "Password must",
    minLength: "Be at least {count} characters",
    upper: "Include an uppercase letter",
    lower: "Include a lowercase letter",
    digit: "Include a digit",
    special: "Include a symbol",
    history: "Differ from the last {count} passwords. The server checks this when you save.",
  },
  adminPolicy: {
    title: "Security policy",
    hint: "These rules appear immediately on sign-in, registration, and password change. Self-service registration stays in the platform console.",
    save: "Save security policy",
    saved: "Security policy saved",
    unchanged: "Security policy is unchanged",
    failed: "Could not save the security policy",
    loadFailed: "Could not load the security policy",
    passwordSection: "Password",
    lockoutSection: "Lockout",
    mfaSection: "Second step",
    sessionSection: "Sessions",
    riskSection: "Risk step-up",
    minLength: "Minimum length",
    requireUpper: "Require an uppercase letter",
    requireLower: "Require a lowercase letter",
    requireDigit: "Require a digit",
    requireSpecial: "Require a symbol",
    historyCount: "Recent passwords to reject",
    maxFailed: "Failed attempts before lockout",
    lockoutMinutes: "Lockout minutes",
    mfaPolicy: "Second step at sign-in",
    mfaOptional: "Follow the account setting",
    mfaRequired: "Require it on every sign-in",
    accessTtl: "Access token seconds",
    refreshTtl: "Refresh token seconds",
    maxDevices: "Devices per application",
    riskEnabled: "Enable risk step-up",
    riskNewIp: "Require a second step from a new IP",
    riskNewDevice: "Require a second step from a new device",
    riskScore: "Step-up score threshold",
    federationSection: "Federation sign-in",
    federationLogin: "Allow configured identity providers to sign in",
    federationLoginHint: "When this is on, the sign-in page shows those providers. Password sign-in stays available.",
    methodsHint: "Allowed sign-in methods come from identity-provider setup. This page only shows them: {methods}.",
  },
  palette: {
    home: "Overview",
    account: "Account",
    apps: "Apps and consents",
    security: "Security center",
    settings: "Settings",
    docs: "Integration docs",
    admin: "Tenant admin",
    users: "Member directory",
    usersHint: "Users panel",
    oidc: "OIDC apps",
    audit: "Audit signal",
    platform: "Platform console",
    systemGroup: "System administration",
    systemGroupHint: "Platform permission group",
    compliance: "Compliance",
    complianceHint: "Compliance page",
    runtime: "Runtime configuration",
    runtimeHint: "Cold archive notes",
    mail: "Mail templates",
    mailHint: "SMTP templates",
    ops: "Queues and logs",
    opsHint: "Platform logs and tenant audit",
    workspace: "Workspace {slug}",
    nav: "Navigate",
    manage: "Admin",
    platformGroup: "Platform",
    tenants: "Tenants",
    placeholder: "Search pages, tenants, or capabilities…",
    search: "Command palette search",
    empty: "No matches. Try a page name, tenant, or workspace slug.",
    hint: "Enter opens the first result · Esc closes · Ctrl/⌘ K opens again",
  },
  boot: {
    title: "Unable to reach the identity service",
    hint: "Setup status could not be read. Check the API, database, and network, then retry.",
    retry: "Check again",
    loading: "Entering the workspace",
  },
  notFound: {
    title: "This page cannot be found",
    hint: "The address may be wrong, or you do not have access to this workspace.",
    back: "Back to overview",
  },
  roles: {
    eyebrow: "Member directory",
    title: "Roles and permissions",
    description: "The reserved Tenant Administrator role is written into this tenant's OIDC token roles. An app that sees tenant_admin is talking to a tenant administrator. Anyone without that role is a regular user. Custom roles can only receive tenant-scoped permissions.",
    create: "Create custom role",
    created: "Custom role created",
    createFailed: "Create failed",
    deleteTitle: "Delete role {name}?",
    deleteHint: "Removes this role from every user and group.",
    deleted: "Role deleted",
    deleteFailed: "Could not delete the role",
    assigned: "Role assigned to the user",
    assignFailed: "Assign failed",
    permsSaved: "Role permissions updated. Users must sign in again before the JWT changes.",
    saveFailed: "Save failed",
    catalogCount: "{count} permissions in the catalog",
    catalogHint: "Only tenant-scoped permissions are listed. The platform permission group lives in the platform console and cannot be assigned to a custom role.",
    reserved: "OIDC visible",
    reservedHint: "Tenant apps use the role name tenant_admin to tell administrators from regular users. This role cannot be deleted, and its permissions stay with the platform catalog.",
    alreadyAssigned: "Already assigned",
    selfAssign: "You cannot change your own access",
    lastAdmin: "The last tenant administrator cannot be removed",
    assignedEyebrow: "Assigned",
    assignedTitle: "People in each permission group",
    assignedHint: "Remove a direct grant here. An account that already has the group cannot be selected again. Removal is audited and can send the event mail.",
    assignedEmpty: "No one has a direct grant for these permission groups yet.",
    assignedLoadFailed: "Unable to read assigned people",
    assignedRemove: "Remove",
    assignedRemoveTitle: "Remove {name} from {role}?",
    assignedRemoveHint: "This person loses the permission group immediately. The change is audited, and event mail is sent when that template is enabled.",
    assignedRemoved: "Removed {name} from {role}",
    loadFailed: "Unable to read roles",
    empty: "No roles yet",
    name: "Name",
    type: "Type",
    system: "System",
    custom: "Custom",
    note: "Description",
    actions: "Actions",
    assignUser: "Assign user",
    permissions: "Permissions",
    delete: "Delete",
    slug: "Identifier (lowercase slug)",
    slugHint: "For example finance_reader",
    displayName: "Display name",
    createOk: "Create",
    assignTitle: "Assign role {name}",
    user: "User",
    assignOk: "Assign",
    permsTitle: "Permissions · {name}",
    close: "Close",
    savePerms: "Save permissions",
    systemReadonly: "System role permissions are maintained by the platform catalog and are read-only.",
    pickPerms: "Choose permissions",
  },
  groups: {
    eyebrow: "Member directory",
    title: "Groups",
    description: "Members inherit roles bound to the group. After sign-in, JWT roles merge direct roles with inherited ones.",
    create: "Create group",
    created: "Group created",
    createFailed: "Create failed",
    deleteTitle: "Delete group {name}?",
    deleted: "Group deleted",
    memberAdded: "Member added",
    addFailed: "Add failed",
    memberRemoved: "Member removed",
    roleBound: "Role bound to the group",
    bindFailed: "Bind failed",
    roleRemoved: "Group role removed",
    loadFailed: "Unable to read groups",
    empty: "No groups yet",
    name: "Name",
    members: "Members",
    roles: "Roles",
    actions: "Actions",
    manage: "Manage",
    delete: "Delete",
    slug: "Identifier (lowercase slug)",
    slugHint: "For example ops-team",
    displayName: "Display name",
    note: "Description",
    createOk: "Create",
    manageTitle: "Manage group {name}",
    pickUser: "Choose a user",
    addMember: "Add member",
    remove: "Remove",
    groupRoles: "Group roles",
    pickRole: "Choose a role",
    bindRole: "Bind role",
    unbind: "Unbind",
  },
  reviews: {
    eyebrow: "Governance",
    title: "Access reviews",
    description: "After creating a review, attest a member or revoke them (revoke disables the account). Last-admin accounts cannot be revoked.",
    placeholder: "Review name, for example 2026-Q3 access review",
    create: "Create review",
    created: "Access review created",
    createFailed: "Create failed",
    attested: "Access kept",
    revoked: "Revoked and account disabled",
    completed: "Review completed",
    loadFailed: "Unable to read access reviews",
    empty: "No access reviews yet. Enter a name to start the first one.",
    name: "Name",
    status: "Status",
    createdAt: "Created",
    actions: "Actions",
    open: "Open",
    titleWith: "Review {name}",
    close: "Close",
    complete: "Mark complete",
    user: "User",
    decision: "Decision",
    attest: "Keep",
    revoke: "Revoke",
  },
  ip: {
    eyebrow: "Access boundary",
    title: "IP rules",
    description: "Deny rules apply to the tenant API. When an allow rule exists, admin APIs accept only matching addresses.",
    created: "IP rule created",
    createFailed: "Create failed",
    cidrRequired: "Enter a CIDR",
    allow: "Allow",
    deny: "Deny",
    reason: "Purpose",
    add: "Add",
    loadFailed: "Unable to read IP rules",
    empty: "No IP rules yet. Add the first allow or deny range.",
    addRule: "Add rule",
    noReason: "No description",
  },
  pager: {
    label: "Pagination",
    range: "{start}–{end} of {total}",
    perPage: "Rows per page",
    custom: "Custom",
    prev: "Previous page",
    next: "Next page",
    page: "Page {page} of {pages}",
  },
  audit: {
    eyebrow: "Audit",
    title: "Audit signal",
    description: "The bar on each row is the severity. Failed actions are no longer marked as success.",
    filter: "Filter by action, label, user, or IP",
    csv: "CSV (table)",
    ndjson: "NDJSON (SIEM stream)",
    cef: "CEF (security device)",
    export: "Export",
    loadFailed: "Unable to read the audit log",
    noMatch: "No matching audit records",
    empty: "No audit records yet",
    time: "Time",
    level: "Level",
    action: "Action",
    eventName: "Event name",
    resource: "Resource",
    user: "User",
    actor: "Operator",
    result: "Result",
    purgeTenant: "Purge audit log",
    purgePlatform: "Purge platform audit",
    purgeTitle: "Confirm audit purge",
    purgeAlert: "Deleted rows cannot be restored from the hot store. An enabled cold archive is written first.",
    purgeRange: "Time range",
    purgeAll: "Everything",
    purgeMode: "Purge mode",
    subject: "Subject",
    subjectPlatform: "Platform audit",
    subjectTenant: "Tenant",
    subjectUser: "User",
    subjectClient: "OIDC application",
    subjectId: "Subject id",
    from: "From",
    to: "To",
    reason: "Reason",
    purgeConfirm: "Purge",
    purgeFailed: "Purge failed",
  },
  saml: {
    eyebrow: "Protocol access",
    title: "SAML Service Provider",
    description: "Register ACS, SLO, certificates, and metadata. Signing certificates can overlap during rotation.",
    register: "Register SP",
    updated: "SAML service provider updated",
    registered: "SAML service provider registered",
    updateFailed: "Update failed",
    registerFailed: "Registration failed",
    disabled: "SAML SP disabled",
    disableFailed: "Disable failed",
    purged: "SAML service provider permanently deleted",
    purgeFailed: "Permanent delete failed",
    rotateTitle: "Rotate the signing certificate for {name}?",
    rotateBody: "The current certificate enters an overlap window (24 hours by default). Provide the new certificate:",
    rotateOk: "Rotate certificate",
    rotated: "SP certificate rotated",
    loadFailed: "Unable to read SAML service providers",
    empty: "No SAML service provider registered yet",
    previousUntil: "Previous certificate valid until {time}",
    overlap: "Certificate overlap",
    edit: "Edit",
    rotate: "Rotate certificate",
    disableTitle: "Disable {name}?",
    disableHint: "New SAML sign-in requests are rejected after disable.",
    disable: "Disable",
    purge: "Delete permanently",
    editTitle: "Edit SAML service provider",
    createTitle: "Register SAML service provider",
    metadataHint: "Import from a metadata URL or XML, or fill the required fields below.",
    name: "Name",
    mapping: "Attribute mapping (JSON object)",
    cert: "X.509 certificate",
    enable: "Enable service provider",
    save: "Save service provider",
    verify: "Verify and register",
    purgeTitle: "Permanently delete SAML service provider",
    irreversible: "This cannot be undone",
    purgeHint: "The service provider and its SAML sessions are physically deleted. Related apps can no longer use SAML sign-in.",
    purgeLead: "Type the service name ",
    purgeTail: " to confirm:",
  },
  oidc: {
    eyebrow: "Protocol access",
    title: "OIDC / OAuth client",
    description: "Manage callbacks, scopes, PKCE, PAR, CIBA, JAR, mTLS, overlapping client-secret rotation, and application owners.",
    register: "Register client",
    registeredTitle: "Client registered",
    secretOnce: "The client secret is shown once",
    updated: "OIDC client updated",
    updateFailed: "Update failed",
    registerFailed: "Registration failed",
    rotateTitle: "Rotate the client secret for {name}?",
    rotateHint: "A new secret is issued. The previous secret stays valid for 24 hours by default so apps can switch. The new secret is shown once.",
    rotateOk: "Rotate secret",
    rotatedTitle: "Client secret rotated",
    newSecretOnce: "The new client secret is shown once",
    previousUntil: "Previous secret valid until {time}",
    newSecret: "New client secret",
    secretSaved: "I saved it",
    rotateFailed: "Rotation failed",
    retireTitle: "Retire the previous secret now?",
    retireHint: "The overlap window ends. Only the new secret can call token, introspect, and revoke. Apps still using the old secret fail immediately.",
    retireOk: "Retire previous secret",
    retired: "Previous secret retired",
    actionFailed: "Action failed",
    purged: "OIDC client permanently deleted",
    purgeFailed: "Permanent delete failed",
    loadFailed: "OAuth client admin API is unavailable",
    empty: "No OIDC client registered yet",
    previousSecretUntil: "Previous secret valid until {time}",
    lastRotated: "Last rotated {time}",
    overlapping: "Overlapping",
    statusEnabled: "Enabled",
    statusPaused: "Paused",
    pausedEyebrow: "Application status",
    pausedSeal: "Paused",
    pausedTitle: "This application is paused",
    pausedLead: "{name} cannot sign you in right now. An administrator paused the application. Your account is unchanged.",
    pausedClient: "Application ID",
    pausedBack: "Back to the workspace",
    pausedMissing: "This application",
    enableStepTitle: "Enable application",
    disableStepTitle: "Pause application",
    enableStepHint: "After you enable {name}, people can sign in through it again. Confirm with step-up first.",
    disableStepHint: "After you pause {name}, sign-in shows a paused page and does not continue. Confirm with step-up first.",
    activitySaved: "Application status updated",
    owner: "Owner",
    ownerNone: "Unassigned",
    transfer: "Transfer owner",
    transferTitle: "Transfer ownership of {name}",
    transferLead: "The new owner must already be an OIDC developer in this workspace. After the transfer, the developer console and this application's tickets follow the new owner.",
    transferCurrent: "Current owner",
    transferPick: "Choose a new owner",
    transferEmpty: "This workspace has no other developer who can receive it.",
    transferOk: "Transfer owner",
    transferSaved: "Owner transferred",
    transferUnchanged: "The owner did not change",
    transferFailed: "Could not transfer the owner",
    candidatesFailed: "Could not load developers",
    ownerNotDeveloper: "Transfer only to a user who already has developer permission in this workspace",
    ownerIneligible: "This developer account cannot receive an application right now",
    ownerChanged: "The owner just changed. Refresh and try again",
    edit: "Edit",
    rotate: "Rotate secret",
    retire: "Retire previous secret",
    purge: "Delete permanently",
    editTitle: "Edit OIDC client",
    createTitle: "Register OIDC client",
    appName: "Application name",
    descriptionField: "Description",
    homepage: "Application homepage",
    logoUrl: "Logo URL",
    logoUpload: "Upload logo",
    logoHint: "PNG, JPEG, or WEBP. An https address is also accepted. When both exist, choose which one is shown first.",
    logoUploaded: "The uploaded image is saved. The URL is kept.",
    logoHeldUrl: "Current URL",
    logoHeldUpload: "Uploaded image",
    logoFormatTitle: "This logo cannot be used",
    logoFormatBody: "Only PNG, JPEG, or WEBP is accepted. Choose another file before uploading.",
    logoUploadOk: "Logo uploaded",
    logoUploadFailed: "Logo was not uploaded",
    logoChoiceTitle: "This application has two logos",
    logoChoiceLead: "Choose which logo is shown first. You can also turn on failure fallback: if either resource is unavailable, the other is used.",
    logoChoiceUrl: "Prefer the URL",
    logoChoiceUpload: "Prefer the uploaded image",
    logoChoiceUploadHint: "Image stored on the platform",
    logoChoiceFallback: "Enable failure fallback",
    logoChoiceFallbackHint: "If either resource is unavailable, use the other one.",
    logoChoiceSave: "Save choice",
    logoChoiceLater: "Decide later",
    logoChoiceSaved: "Logo preference saved",
    logoChoiceFailed: "Could not save the logo preference",
    logoChoiceEdit: "Change logo preference",
    grantTypes: "Grant types",
    homepageExtra: "The open link in Available apps uses this address. Leave it blank to hide the shortcut.",
    homepageInvalid: "Enter a full address that starts with http:// or https://",
    redirects: "Redirect URIs (one per line)",
    logoutRedirects: "Post-logout redirect URIs (one per line)",
    scopes: "Allowed scopes (space separated)",
    authMethod: "Token endpoint authentication",
    subjectDn: "Certificate subject (Subject DN)",
    subjectRequired: "Certificate authentication requires a registered Subject DN",
    accessTtl: "Access token TTL (seconds)",
    refreshTtl: "Refresh token TTL (seconds)",
    publicClient: "Public client",
    forcePkce: "Require proof key exchange",
    forcePar: "Require pushed authorization",
    forceMfa: "Require MFA",
    sessionMode: "Sign-in occupancy",
    sessionMulti: "Allow multiple devices",
    sessionSingle: "One device only; a new sign-in displaces the previous one",
    sessionModeExtra: "This does not affect other applications. One device signs out this application's other devices and leaves a displacement notice.",
    singleSession: "One device",
    ciba: "Backchannel delivery",
    cibaPoll: "poll (client polls /token)",
    cibaPing: "ping (OP notifies auth_req_id)",
    cibaPush: "push (OP pushes the token)",
    cibaUrl: "CIBA notification URL (HTTPS)",
    cibaUrlRequired: "ping and push need an HTTPS notification endpoint",
    cibaUrlExtra: "ping POSTs auth_req_id only. push POSTs the token or an error. It must be public HTTPS, not localhost.",
    jar: "Signed authorization key set",
    jarExtra: "Register RS256 or ES256 public keys for signed authorization requests. Every key needs a kid.",
    enable: "Enable client",
    save: "Save client",
    registerGenerate: "Register and generate credentials",
    purgeTitle: "Permanently delete OIDC client",
    irreversible: "This cannot be undone",
    purgeHint: "Authorization codes, consents, token records, and refresh sessions are deleted. Deployed apps lose refresh and re-authorization immediately.",
    purgeLead: "Type the application name ",
    purgeTail: " to confirm:",
  },
  federation: {
    eyebrow: "Federation",
    title: "External identity providers",
    description: "Configure Google, GitHub, Microsoft, Apple, OIDC, or Okta. Secrets are stored encrypted. OIDC and Okta can discover endpoints from the issuer alone.",
    add: "Add provider",
    saved: "Federation provider saved (secret stored encrypted)",
    saveFailed: "Save failed",
    deleteTitle: "Delete this federation provider?",
    deleteHint: "It disappears from tenant sign-in immediately. Linked accounts are not unbound, but they can no longer sign in through this provider.",
    deleteOk: "Delete provider",
    deleted: "Provider deleted",
    validated: "Validation passed",
    validateFailed: "Validation failed",
    loadFailed: "Unable to read federation configuration",
    empty: "Federation sign-in is not configured yet",
    status: "Status",
    enabled: "Enabled",
    disabled: "Disabled",
    actions: "Actions",
    validate: "Validate",
    delete: "Delete",
    modalTitle: "Configure federation provider",
    type: "Type",
    emailDomains: "Email domains",
    emailDomainsHint: "One domain per line, for example acme.com. Sign-in matches the full domain and does not guess a parent domain.",
    domains: "Email domains",
    oidcGeneric: "Generic OIDC (discovery)",
    okta: "Okta / enterprise OIDC template",
    appleSecret: "Private key (.p8 PEM)",
    redirect: "Redirect URI (optional, portal callback by default)",
    entra: "Entra tenant (optional, common by default)",
    entraTip: "common, organizations, or a directory tenant ID",
    issuer: "Issuer URL",
    issuerExtra: "If authorize, token, and userinfo are empty, /.well-known/openid-configuration is fetched.",
    issuerRequired: "OIDC and Okta need an issuer",
    teamId: "Team ID",
    teamTip: "Apple Developer Team ID",
    keyId: "Key ID",
    keyTip: "Sign in with Apple Key ID (stored in the authorize_url field)",
    scopesOptional: "Scopes (optional)",
    issuerOptional: "Issuer (optional)",
    enable: "Enabled",
    save: "Save",
  },
  protocolUi: {
    issuingRegion: "Issuing region",
    issuingRegionHint: "Region label written when the session is created. Cross-region refresh is rejected.",
    requestUri: "Authorization request reference",
    requestUriHint: "request_uri returned by PAR, so the address bar does not carry every authorization parameter.",
    par: "Pushed authorization (PAR)",
    parHint: "Push authorization parameters to the server, then open sign-in with the reference, so the browser address bar leaks less.",
    pkce: "Proof key exchange (PKCE)",
    mTLS: "Mutual TLS (mTLS)",
    mTLSHint: "Use a client TLS certificate instead of a shared secret. The subject DN must match the edge proxy.",
    ciba: "Backchannel authentication (CIBA)",
    jar: "Signed authorization request (JAR)",
    jwks: "Public key set (JWKS)",
    issuerPar: "Issuer PAR",
  },
  helpers: {
    jsonObject: "Enter a JSON object",
    jsonInvalid: "Enter a valid JSON object",
    jwksShape: "JWKS must be a JSON object with a keys array",
    jwksInvalid: "Enter valid JWKS JSON",
  },
  apiErrors: {
    fallback: "Action failed",
    csrf: "The security token expired. Refresh the page and try again.",
    upstream: "The identity service is temporarily unavailable. Try again shortly.",
    timeout: "The request timed out. Try again shortly.",
    network: "The network connection failed. Check it and try again.",
    maintenance: "The identity service is in maintenance. Try again later.",
  },
  sources: {
    ldap: "Enterprise LDAP",
    unknownProvider: "Unknown identity provider",
    oidcApp: "OIDC app · {id}",
    federation: "Federated identity · {name}",
    password: "Password sign-in",
    unknown: "Unknown source",
  },
  recovery: {
    confirmTitle: "Confirm this email",
    confirmHint: "Opening the link does not verify the address. Continue only if you requested this message.",
    confirmVerify: "Verify this email",
    verifying: "Verifying email",
    verifyingHint: "Checking the one-time verification link.",
    verified: "Email verified",
    verifiedHint: "You can continue sign-in with the verified email.",
    backLogin: "Back to sign-in",
    linkInvalid: "Verification link is invalid or expired",
    linkInvalidHint: "Sign in and send the verification email again.",
    resetMissing: "The reset link is missing required parameters",
    resetFailed: "Password reset failed",
    updated: "Password updated",
    updatedHint: "Older sign-in sessions were signed out. Sign in with the new password.",
    goLogin: "Go to sign-in",
    eyebrow: "Account recovery",
    resetTitle: "Set a new password",
    resetIntro: "After the new password takes effect, other devices are signed out immediately.",
    newPassword: "New password",
    passwordMin: "Password needs at least 8 characters",
    confirmPassword: "Confirm new password",
    confirmRequired: "Enter the new password again",
    mismatch: "The two passwords do not match",
    update: "Update password",
    inviteMissing: "The invitation link is missing required parameters",
    inviteInvalid: "Invitation is invalid or expired",
    acceptFailed: "Could not accept the invitation",
    inviteUnavailable: "Invitation unavailable",
    reading: "Reading the invitation",
    wait: "Please wait.",
    joined: "Joined the workspace",
    joinedHint: "Account {email} was created. Sign in with the new password.",
    inviteEyebrow: "Member invitation",
    acceptTitle: "Accept invitation",
    acceptLead: "You are invited to join ",
    roleAdmin: "tenant admin",
    roleMember: "member",
    acceptMid: ". Email ",
    acceptTail: " will be the sign-in identifier.",
    acceptUntil: " Invitation valid until {time}.",
    username: "Username",
    usernameMin: "Username needs at least 3 characters",
    displayName: "Display name",
    setPassword: "Set password",
    passwordMinShort: "Password needs at least 8 characters",
    confirmShort: "Confirm password",
    confirmAgain: "Enter the password again",
    createJoin: "Create account and join",
    thesis: "Recover access to your identity safely.",
    thesisBody: "One-time links, the password policy, and full session revocation protect account recovery.",
  },
  setup: {
    connect: "Connect services",
    domains: "Access domains",
    admin: "Root administrator",
    confirm: "Confirm and enable",
    restartTitle: "Setup finished. Restart the service.",
    restartBody: "Run ./manage.sh restart applications to load the encrypted install configuration, then open the sign-in page.",
    gotIt: "Got it",
    initialized: "OwO! Identity is initialized",
    already: "Setup is already complete. Opening sign-in.",
    failed: "Setup failed",
    badge: "First-deploy wizard",
    thesis: "Build a trusted identity foundation.",
    thesisBody: "Every connection is checked before it is saved. After setup, this entry closes permanently.",
    step: "Step {current} / {total}",
    connectTitle: "Connect persistence",
    connectHint: "Configure PostgreSQL and Redis. Credentials are stored encrypted and are not written to the repository.",
    redisAddr: "Redis address",
    redisPassword: "Redis password",
    boundaryTitle: "Define the trusted boundary",
    boundaryHint: "These domains are used for the portal, OIDC callbacks, SAML ACS, and CORS checks.",
    externalUrl: "External service URL",
    adminHost: "Platform admin host",
    origins: "Allowed domains and origins",
    adminTitle: "Create the root administrator",
    adminHint: "This account has platform-wide access. The password needs at least 12 characters.",
    tenantName: "Primary tenant name",
    username: "Username",
    displayName: "Display name",
    email: "Email",
    password: "Password",
    enableTitle: "Verify and enable",
    enableHint: "The system tests connections, runs database migrations, and creates the root administrator.",
    once: "Setup runs once",
    onceHint: "After this, /setup returns 404 like any missing address. Later connection changes need a super admin and a service restart.",
    checkStores: "PostgreSQL and Redis are filled in",
    checkDomains: "Trusted domain boundary is defined",
    checkAdmin: "Root administrator details are confirmed",
    confirmLabel: "I confirm enabling OwO! Identity",
    confirmRequired: "Confirm the setup action",
    confirmed: "Confirmed",
    unconfirmed: "Not confirmed",
    back: "Back",
    next: "Continue",
    finish: "Verify and initialize",
    unreachable: "Unable to reach the setup service",
  },
  docs: {
    eyebrow: "Integrate",
    title: "Integration docs",
    description: "Read the account, protocol, tenant, and platform guides you are allowed to use.",
    nav: "Documentation",
    issuer: "Issuer for this workspace",
    copy: "Copy",
    copied: "Copied",
    method: "Method",
    path: "Path",
    auth: "Auth",
    params: "Parameters",
    param: "Name",
    where: "In",
    required: "Required",
    detail: "Description",
    yes: "Yes",
    no: "No",
    request: "Request example",
    responses: "Response examples",
    successZone: "Successful responses",
    failureZone: "Failed responses",
  },
  develop: {
    title: "Apply to develop OIDC apps for {tenant}",
    lead: "After approval you can register and manage public OIDC apps you create. You cannot manage other people's apps or workspace security policy.",
    register: "Register and apply",
    login: "Sign in with an existing account",
    loginExisting: "Sign in with an existing account",
    closedTitle: "This workspace is not open for registration",
    closedBody: "Public registration is closed. An existing account can still sign in and apply. Invitations are not affected.",
    otherWorkspace: "You are signed in to {current}. To apply here, sign in or register an account in this workspace.",
    missing: "That workspace was not found.",
    policyFailed: "The registration status could not be loaded",
    loadFailed: "The application status could not be loaded",
    already: "You are already an OIDC application developer in this workspace.",
    openConsole: "Open the developer console",
    pending: "A tenant administrator still needs to review this request.",
    ineligible: "This account cannot apply. Verify the email address and confirm the account is not disabled or banned.",
    statement: "Describe the application you want to connect",
    submit: "Submit request",
    submitReview: "Submit for review",
    submittedReview: "Submitted for review",
    pendingTitle: "Applications waiting for review",
    approvedTitle: "Approved applications",
    noRequests: "No application is waiting for review.",
    clientReviewTitle: "OIDC application review",
    noClientReviews: "No application requests yet.",
    secretOnce: "The client secret is shown only once. The application cannot be used before approval.",
    consoleEyebrow: "Developer",
    consoleTitle: "My OIDC applications",
    consoleLead: "A creation request waits for a tenant or platform administrator. Until it is approved, related requests are rejected.",
    create: "Create application",
    created: "Created",
    save: "Save",
    saved: "Saved",
    saveFailed: "The application was not saved",
    clientsFailed: "Applications could not be loaded",
    clientsEmpty: "You have not created an application yet.",
    client: "Application",
    oid: "OID",
    platformCheck: "Platform review",
    platformVerified: "Yes",
    state: "Status",
    active: "Enabled",
    paused: "Paused",
    actions: "Actions",
    edit: "Edit",
    clientName: "Name",
    clientDescription: "Description",
    homepage: "Homepage",
    redirects: "Redirect URLs",
    redirectsHint: "One per line. Public URLs use https. Localhost may use http.",
    enabled: "Enabled",
    queueEyebrow: "Requests",
    queueTitle: "Developer requests",
    queueLead: "Share the request link. After approval, the person can manage only applications they create.",
    copyLink: "Copy request link",
    linkCopied: "Copied",
    linkFailed: "The link was not copied",
    queueFailed: "Requests could not be loaded",
    queueEmpty: "No requests yet.",
    applicant: "Applicant",
    submittedAt: "Submitted",
    approve: "Approve",
    approved: "Approved",
    reject: "Reject",
    rejected: "Rejected",
    revoke: "Revoke",
    revoked: "Revoked",
    decideFailed: "The decision was not saved",
    reason: "Write the reason",
    statusPending: "Pending",
    statusApproved: "Approved",
    statusRejected: "Rejected",
    statusRevoked: "Revoked",
  },
  dossier: {
    eyebrow: "Super view",
    title: "User dossiers",
    lead: "Review grants, devices, and audit for users in every workspace. The dossier is read-only. Size is an estimate, not a bill.",
    search: "Username, email, or display name",
    tenant: "Workspace slug",
    anyStatus: "Any status",
    loadFailed: "Users could not be loaded",
    empty: "No matching users.",
    detailFailed: "The dossier could not be loaded",
    pick: "Select a row to open the dossier.",
    person: "Person",
    workspace: "Workspace",
    state: "Status",
    lastLogin: "Last sign-in",
    email: "Email",
    roles: "Roles",
    apps: "Authorized apps",
    devices: "Sign-in devices",
    footprint: "Estimated size",
    estimate: "About {size}",
    groups: "Groups",
    mfa: "Second step",
    federation: "Linked identities",
    audit: "Audit",
    sameEmail: "Other workspaces",
    parts: "Size breakdown",
    manage: "Manage in this workspace",
    back: "Back to the list",
    more: "{count} more",
    exact: "{bytes} bytes",
    none: "None",
    status: {
      active: "Active",
      inactive: "Inactive",
      banned: "Banned",
      locked: "Locked",
      pending_deletion: "Pending deletion",
    },
    part: {
      profile: "Profile",
      emails: "Emails",
      addresses: "Addresses",
      sessions: "Sessions",
      consents: "Grants",
      audits: "Audit",
      federations: "Federation",
      webauthn: "Passkeys",
      backup_codes: "Backup codes",
      invitations: "Invitations",
      owned_clients: "Owned apps",
    },
  },
  load: {
    count: "Count",
    unit: "Unit",
    closer: "Zoom to 1 minute",
    failed: "Unable to read load",
    empty: "No requests in this window",
    requests: "Requests",
    bytesIn: "Bytes in",
    bytesOut: "Bytes out",
    errors: "Errors",
    latency: "Average latency",
    latencyValue: "{count} ms",
    warnRequest: "Warning",
    errorRequest: "Error",
    when: "Time",
    route: "Route",
    status: "Status",
    source: "Source",
    sourceTitle: "Request source",
    detailExpired: "This window is older than 72 hours, so only the aggregate is available.",
    units: { second: "Second", minute: "Minute", hour: "Hour", day: "Day", month: "Month" },
  },
  assets: {
    eyebrow: "Assets",
    title: "Occupancy and data",
    description: "See the process, cache, and allowlisted tables. Release runtime cache, or delete runtime logs and files that are no longer referenced.",
    refresh: "Refresh",
    usageHint: "These figures are the process and the data stores, not the host network card or disk.",
    usageFailed: "Unable to read occupancy",
    heap: "Heap",
    goroutines: "Goroutines",
    redisMemory: "Redis memory",
    assetBytes: "Uploaded files",
    table: "Table",
    viewUsage: "Occupancy",
    viewTables: "Tables",
    viewCache: "Runtime cache",
    viewReclaim: "Clear occupancy",
    viewLogo: "Logo limits",
    viewBackup: "Backups",
    backup: {
      title: "Scheduled backups",
      hint: "Pack the database, uploaded files, and signing keys on a schedule. A backup is deleted when its slot is needed or its lifetime ends. Redis, the installation secret, and the decryption password stay out of the package.",
      failed: "Unable to read backups",
      empty: "No backups yet. Save a decryption password, then run one now or wait for the schedule.",
      auto: "Automatic backup",
      interval: "Backup interval",
      slots: "Maximum slots",
      retention: "Lifetime of each backup",
      password: "Decryption password",
      passwordHint: "At least 12 characters. Automatic backups are encrypted with it, and a download asks for it again.",
      passwordKeep: "Leave this blank to keep the current decryption password. A new one applies to future backups; older backups keep the password from when they were made.",
      hourSuffix: "hours",
      hours: "{count} hours",
      days: "{count} days",
      retentionGap: "The lifetime is shorter than the interval. The previous backup may expire before the next one is written.",
      save: "Save settings",
      run: "Back up now",
      running: "A backup is being written.",
      magazine: "Backup slots",
      slot: "Slot {index}",
      slotEmpty: "Empty",
      slotReady: "Ready",
      slotRunning: "Writing",
      slotFailed: "Failed",
      trigger: "Source",
      triggerSchedule: "Automatic",
      triggerManual: "Manual",
      created: "Created",
      expires: "Expires",
      pending: "Set when the backup finishes",
      size: "Stored size",
      contentsLabel: "Contents",
      contents: "{tables} tables · {files} files · {keys} signing keys",
      keySet: "Set {time}",
      sha: "Checksum",
      download: "Download backup",
      extras: "{count} more backups are outside the current slots.",
      policyTitle: "Save backup settings",
      policyBody: "Write the reason and enter the secondary password. The interval, slots, lifetime, or decryption password change only after that.",
      downloadTitle: "Download backup",
      downloadBody: "Enter the decryption password from when this backup was made, and your secondary password. A wrong decryption password does not use up the secondary confirmation.",
      decrypt: "Decryption password",
      remove: "Delete backup",
      removeTitle: "Delete backup",
      removeBody: "Write the reason and enter the secondary password. This backup is deleted after that.",
      deleted: "Backup deleted",
      confirm: "Confirm",
      saved: "Backup settings saved",
      started: "Backup started",
      downloaded: "Backup downloaded",
      mismatch: "That decryption password does not match this backup",
      locked: "Too many decryption attempts. Try again later",
      inProgress: "A backup is already being written",
      noPassword: "Set a decryption password first",
      notify: "Email notice",
      notifyHint: "Turn this on to choose the cycle. At the end of each cycle, super administrators with a verified email address get one message about backup downloads and updates in that cycle. A quiet cycle sends nothing.",
      notifyInterval: "Notice cycle",
    },
    noSql: "Tables come from the current database. Secret columns stay hidden. Only marked columns can be changed. SQL is not available.",
    search: "Search this table",
    tenantFilter: "Tenant ID",
    rows: "{count} rows",
    readOnly: "Read only",
    canEdit: "Marked columns can be changed",
    canRevoke: "Revoke only, fields stay fixed",
    columns: "Visible columns",
    yes: "Yes",
    no: "No",
    record: "Record",
    revoke: "Revoke",
    deleteRow: "Delete",
    fieldHint: "Only columns this table allows you to change are listed.",
    assetTables: {
      _ops_drill_marker: "Ops drill marker",
      access_review_items: "Access review items",
      access_reviews: "Access reviews",
      announcement_reads: "Announcement reads",
      announcements: "Announcements",
      audit_archive_batches: "Audit archive batches",
      audit_logs: "Tenant audit",
      audit_outbox: "Audit delivery queue",
      authorization_codes: "OIDC authorization codes",
      authorization_interactions: "One-time login and consent tickets; raw token is not stored",
      backup_codes: "Backup recovery codes",
      bulk_operation_logs: "Bulk operation log",
      ciba_auth_requests: "CIBA authentication requests",
      developer_applications: "Developer applications",
      developer_client_requests: "Developer client requests",
      device_codes: "Device codes",
      email_templates: "Email templates",
      external_identities: "Linked external identities",
      federation_providers: "Federation providers",
      goose_db_version: "Schema migration versions",
      group_members: "Group members",
      group_roles: "Roles granted to groups",
      groups: "Groups",
      ip_rules: "IP access rules",
      legal_acceptances: "Legal read records",
      legal_document_versions: "Legal document versions",
      legal_documents: "Legal documents",
      legal_notice_deliveries: "Legal notice deliveries",
      legal_read_deferrals: "Deferred legal prompts",
      mfa_challenges: "MFA login challenges",
      oauth_client_verifications: "Manual application reviews",
      oauth_clients: "OIDC applications",
      oauth_tokens: "Issued tokens",
      password_history: "Password history",
      password_reset_tokens: "Password reset tickets",
      permissions: "Permission catalog",
      platform_assets: "Uploaded files",
      platform_audit_logs: "Platform audit",
      platform_logs: "Runtime logs",
      platform_maintenance: "Maintenance mode",
      platform_notice_policy: "Notice policy",
      platform_backup_policy: "Platform backup schedule",
      platform_backup_keys: "Backup decryption keys",
      platform_backups: "Platform backups",
      payment_bills: "Payment bills",
      payment_consents: "Payment consents",
      payment_fee_profile_overrides: "Payment fee profile overrides",
      payment_invoices: "Payment invoices",
      payment_methods: "Payment methods",
      payment_stripe_configs: "Stripe payment configuration",
      payment_stripe_keys: "Stripe keys",
      withdrawal_requests: "Withdrawal requests",
      pushed_authorization_requests: "Pushed authorization requests",
      risk_signals: "Login risk signals",
      role_permissions: "Permissions on a role",
      roles: "Permission groups",
      saml_service_providers: "SAML service providers",
      saml_sessions: "SAML sessions",
      security_notices: "Security notices",
      sessions: "Sign-in sessions",
      support_idempotency: "Support idempotency records",
      support_notifications: "Support notifications",
      support_ticket_feedback: "Ticket feedback",
      support_ticket_handlers: "Ticket handlers",
      support_ticket_messages: "Ticket messages",
      support_tickets: "Support tickets",
      tenants: "Tenants",
      traffic_buckets: "Traffic totals",
      traffic_samples: "Traffic samples",
      user_addresses: "Postal addresses",
      user_consents: "Application consents",
      user_emails: "Additional email addresses",
      user_invitations: "Invitations",
      user_preferences: "Account preferences",
      user_profiles: "Profiles",
      user_roles: "Direct role grants",
      user_step_up_credentials: "Step-up credentials",
      users: "Users",
      verification_tokens: "Email verification tickets",
      webauthn_credentials: "Passkeys",
    },
    clearLogs: "Clear the last 24 hours of runtime logs",
    failed: "Unable to read assets",
    empty: "No rows to show",
    summary: "Summary",
    when: "Time",
    actions: "Actions",
    change: "Change",
    remove: "Delete or revoke",
    cacheTitle: "Runtime cache",
    cacheHint: "Clear one prefix at a time. Sign-in locks and the block list stay when runtime cache is released.",
    keys: "{count} keys",
    clearCache: "Clear",
    reclaimTitle: "Runtime occupancy",
    reclaimHint: "Releasing cache does not delete audit records. Choose a range and enter the step-up password before deleting logs or files.",
    reclaimFailed: "Reclaimable occupancy could not be read",
    reclaimEmpty: "No runtime occupancy to clear",
    reclaimReading: "{count} items · {size}",
    reclaimSizeUnknown: "size unavailable",
    releaseCache: "Runtime cache",
    releaseCacheHint: "Return process heap, and clear address-lookup cache and unused step-up tokens.",
    release: "Release",
    reclaimLogs: "Runtime logs",
    reclaimLogsHint: "Deletes only the platform's own runtime logs. Audit records stay in audit purge.",
    deleteLogs: "Delete logs",
    reclaimFiles: "Unreferenced files",
    reclaimFilesHint: "Deletes logo files in the upload directory that no longer have a record.",
    deleteFiles: "Delete files",
    horizon: "Delete range",
    horizon24: "Older than 24 hours",
    horizon7: "Older than 7 days",
    horizonAll: "All runtime logs",
    cacheReleased: "Runtime cache released",
    filesCleared: "Unreferenced files deleted",
    logoLimits: "Logo limits",
    maxBytes: "Maximum size",
    maxEdge: "Longest edge",
    byteSuffix: "bytes",
    pixelSuffix: "px",
    edgeReading: "{count} px",
    saveLimits: "Save limits",
    limitsSaved: "Logo limits saved",
    confirmTitle: "Confirm the risk",
    confirmBody: "Write the reason and enter the step-up password. The change applies to the selected object and is written to the platform audit.",
    field: "Field",
    value: "New value",
    reason: "Reason",
    confirm: "Apply",
    updated: "Updated",
    deleted: "Deleted",
    cacheCleared: "Cache cleared",
    logsCleared: "Runtime logs cleared",
  },
  stepUp: {
    title: "Step-up password",
    hint: "Tenant administrators and platform super administrators use it before risk actions, such as changing a table, clearing cache, clearing runtime logs, and purging audit logs. It must differ from the sign-in password. An enabled authenticator can be used instead.",
    rail: "Step-up",
    confirmHint: "Enter the step-up password. A bound authenticator can confirm this same action.",
    configured: "Step-up password is set",
    missing: "Step-up password is not set",
    methods: "Authenticator {totp}, passkey {passkey}",
    on: "on",
    off: "off",
    loginPassword: "Current sign-in password",
    newPassword: "New step-up password",
    different: "Must differ from the sign-in password",
    save: "Save step-up password",
    clear: "Clear step-up password",
    saved: "Step-up password saved",
    cleared: "Step-up password cleared",
    failed: "Step-up password change failed",
    password: "Step-up password",
    totp: "Authenticator",
    method: "Confirmation",
    code: "Password or code",
    passkeyHint: "A registered passkey can confirm this same action.",
    usePasskey: "Use passkey",
    passkeyFailed: "Passkey confirmation did not finish. Confirm this device has a registered passkey, then try again.",
  },
  geo: {
    locate: "Locate",
    ip: "IP",
    private: "Private address",
    unavailable: "Local address database is unavailable",
    unknown: "No location on record",
    failed: "Lookup failed",
  },
  compose: {
    minimize: "Minimize",
    expand: "Full screen",
    restore: "Restore",
    close: "Close",
  },
  support: {
    eyebrow: "Desk",
    title: "Tickets",
    description: "Any tenant account can open a platform ticket and continue the conversation here.",
    mine: "My tickets",
    queue: "Queue",
    empty: "No tickets yet",
    missing: "This ticket could not be found",
    created: "Ticket submitted",
    failed: "The ticket action failed",
    create: "Submit ticket",
    openComposer: "Create ticket",
    sectionCompose: "Open a ticket",
    sectionThread: "Conversation",
    sectionActions: "Handling",
    sectionFeedback: "Feedback",
    scope: "Subject",
    scopePlatform: "Whole platform",
    scopeTenant: "This tenant",
    scopeApp: "One OIDC application",
    scopeValue: { platform: "Whole platform", tenant: "This tenant", oidc_client: "OIDC application" },
    source: "Source",
    portalSource: "Portal",
    targetApp: "Related application",
    category: "Category",
    categoryValue: { sign_in: "Sign-in", account: "Account", mfa: "MFA", application: "Application", other: "Other" },
    ticketTitle: "Title",
    body: "Initial reason",
    requester: "Requester",
    tenant: "Tenant",
    opened: "Opened",
    firstResponse: "First reply",
    closed: "Closed",
    handlers: "Handlers",
    unassigned: "Unassigned",
    inactive: " (inactive)",
    reply: "Reply",
    send: "Send",
    internal: "Internal note",
    reason: "Reason (at least 8 characters)",
    feedback: "Send feedback",
    feedbackSaved: "Feedback saved",
    satisfaction: "Satisfaction",
    resolved: "Resolved",
    resolvedValue: { yes: "Resolved", partial: "Partly resolved", no: "Not resolved" },
    service: "Service rating",
    comment: "Comment",
    status: { submitted: "Submitted", reviewed: "Reviewed", in_progress: "In progress", pending_feedback: "Awaiting feedback", closed: "Closed" },
  },
  announcements: {
    eyebrow: "Platform news",
    title: "Announcements",
    description: "Super administrators write announcements and choose the audience when publishing.",
    detailDescription: "Announcement details and reading status",
    backToList: "Back to announcements",
    empty: "No announcements to read",
    saved: "Announcement saved",
    failed: "The announcement action failed",
    itemTitle: "Title",
    body: "Body",
    severity: "Severity",
    severityValue: { info: "Info", warning: "Warning", critical: "Critical" },
    audience: "Audience",
    audienceValue: { tenant_admins: "Tenant administrators", oidc_developers: "OIDC developers", all_users: "All signed-in users" },
    saveDraft: "Save draft",
    sectionList: "Announcement list",
    sectionCompose: "Write an announcement",
    sectionDetail: "Announcement",
    publisher: "Published by",
    attachments: "Attachments and images",
    attachmentHint: "Up to 50 MiB per file and 10 files. Images, PDFs, audio, and video are supported.",
    attachmentTooLarge: "Each attachment must be 50 MiB or smaller",
    attachmentLimit: "An announcement can have at most 10 attachments",
    removeAttachment: "Remove",
    previewImage: "View image: {name}",
    downloadAttachment: "Download {name}",
    download: "Download",
    mediaUnsupported: "This browser cannot play this media.",
    imageCount: "{count} attachments",
    imageLabel: "Image {count}",
    readReport: "Audience reading status",
    readReportLoading: "Loading read status",
    readReportFailed: "Could not load announcement read status",
    readTotal: "Overall read {read} / {total}",
    readCounts: "Read {read} · Unread {unread}",
    readRate: "Read rate",
    statusValue: { published: "Published", archived: "Archived" },
    renotifyAction: "Notify unread users again",
    renotifyQueued: "Notification queued for {count} unread users",
    renotifyUnavailable: "SMTP delivery is disabled for this severity or SMTP is not configured",
    publish: "Publish",
    openComposer: "Publish announcement",
    archive: "Archive",
    draft: "Draft",
    view: "View",
    dismiss: "Hide for now",
    read: "Read",
    unread: "Unread",
    unreadCount: "{count} unread",
    confirmRead: "I have read this",
    readConfirmed: "Reading confirmed",
    later: "Read later",
  },
  maintenance: {
    eyebrow: "Availability",
    title: "Maintenance",
    description: "When enabled, only a super administrator can sign in. API groups can be opened again.",
    on: "Maintenance is on",
    off: "Maintenance is off",
    defaultHint: "The sign-in page uses the default notice when this is empty.",
    never: "No change recorded yet",
    toggle: "Maintenance switch",
    editGroups: "Choose open APIs",
    confirmTitle: "Confirm maintenance policy",
    publicMessage: "Sign-in notice",
    reason: "Reason",
    groups: "API groups to open",
    probeWarning: "Health checks start closed. A load balancer may mark the node unhealthy. Select probes if you need them.",
    save: "Save maintenance policy",
    saved: "Maintenance policy saved",
    failed: "Maintenance policy was not saved",
    holdTitle: "The identity service is in maintenance",
    holdHint: "Your sign-in session is kept. You can continue when maintenance ends.",
    group: {
      probes: "Health and metrics",
      discovery: "Discovery and JWKS",
      oidc: "OIDC",
      saml: "SAML",
      scim: "SCIM",
      federation: "Federation",
      auth_public: "Registration and password reset",
      portal: "Signed-in portal",
      tenant_admin: "Tenant administration",
      developer: "Developers",
      platform: "Platform console",
      assets: "Public logos and avatars",
    },
  },
  legal: {
    terms: "Terms of use",
    privacy: "Privacy notice",
    impressum: "Impressum",
    paymentPolicy: "Payment Policy",
    library: "Notices",
    libraryHint: "Current platform notices and tenant-specific documents, gathered in one place.",
    thirdParty: "Third-party application",
    verified: "平台人工校验通过",
    grant: "Grant manual review",
    revoke: "Remove manual review",
    reason: "Reason",
    reasonHint: "At least 8 characters describing the review.",
    verifySaved: "Review mark updated",
    verifyFailed: "Review mark was not updated",
    agreeTerms: "I agree to the terms of use, version {version}",
    acknowledgePrivacy: "I have read privacy notice version {version}. This is not consent to processing required for the service.",
    agreeDeveloper: "I agree to {title}, version {version}",
    confirmTitle: "Confirm the current version",
    confirmLead: "A version changed. Confirm it before entering the workspace.",
    continue: "Confirm and continue",
    loading: "Loading the current version",
    reading: "Opening the document",
    loadFailed: "The document did not open",
    unavailable: "This document is not available. It may have been withdrawn.",
    signIn: "Sign in before reading this document",
    developerLead: "Read and confirm these notices before applying to become a developer.",
    confirmDeveloper: "Confirm the developer notices",
    registerRequired: "Read and check the terms and the privacy notice first",
    deskEyebrow: "Document versions",
    deskTitle: "Legal documents",
    deskHint: "Publishing notifies the people affected. A published body cannot be edited; a change starts a new version.",
    body: "Body",
    changelog: "What changed",
    saveDraft: "Save draft",
    submit: "Submit revision",
    publish: "Publish",
    withdraw: "Withdraw",
    saved: "Saved",
    saveFailed: "Not saved",
    readOnly: "This document is read-only for you. A tenant administrator can edit this tenant's notice. A platform administrator without that role can read and withdraw it, but cannot rewrite it.",
    statusDraft: "Draft",
    statusSubmitted: "Submitted",
    statusPublished: "Published",
    statusWithdrawn: "Withdrawn",
    platformTab: "Legal documents",
    adminTab: "Developer notice",
    versionsTab: "Versions",
    readsTab: "Read ledger",
    readsEyebrow: "Read ledger",
    readsTitle: "Who has read the platform documents",
    readsHint: "Counts use the current published version. People without a read record see a prompt on the next sign-in until they confirm. Asking to be reminded later is not a read.",
    readsLoadFailed: "The read ledger did not open",
    readsEmpty: "No platform document is published yet",
    readCount: "Read {count}",
    unreadCount: "Unread {count}",
    rosterEmpty: "Nobody matches this filter",
    filterAll: "All",
    filterRead: "Read",
    filterUnread: "Unread",
    readAt: "Read at",
    waiting: "Not recorded",
    reader: "Person",
    workspace: "Workspace",
    promptTitle: "Confirm that you have read these",
    promptLead: "These platform documents are not recorded as read. Each sign-in shows this prompt until you confirm.",
    confirmRead: "I have read these",
    markRead: "I have read this",
    confirmReadItem: "I have read {title}, version {version}",
    later: "Remind me later",
    laterHint: "This sign-in will not ask again. The next sign-in will, until you confirm.",
    promptFailed: "The read was not recorded",
    laterFailed: "The reminder was not delayed",
    promptRetry: "Retry",
  },
  errors: {
    boundaryTitle: "This page did not finish rendering",
    boundaryHint: "The rest of the navigation still works. Retry mounts the page again.",
    retry: "Retry",
  },
} as const;
